'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cn } from '@ecom/helper-react-class-combine';
import { Plus, Minus } from 'lucide-react';
import type { FilterOption } from '@/features/games/internal/types/category-filters';
import { FilterCheckboxItem } from '@/features/games/internal/components/layouts/filter-checkbox-item';
import { RatingFilterItem } from '@/features/games/internal/components/layouts/rating-filter-item';
import { FilterModal } from '@/features/games/internal/components/layouts/filter-modal';
import { useSearchParams } from 'next/navigation';
import { useRouter } from '@/i18n/routing';
import { useListingNavigation } from '@/shared/hooks/use-listing-navigation';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import { useTranslations } from 'next-intl';

const DEFAULT_VISIBLE_COUNT = 10;

type FilterSectionProps = {
  title: string;
  filterType: string;
  options: FilterOption[];
  defaultOpen?: boolean;
  showClear?: boolean;
};

export const FilterSection = ({
  title,
  filterType,
  options,
  defaultOpen = false,
  showClear = true,
}: FilterSectionProps) => {
  const t = useTranslations('Categories');
  const router = useRouter();
  const { buildListingHref } = useListingNavigation();
  const searchParams = useSearchParams();
  const { showOverlay } = useOverlayStore();

  const modalKey = `filter-modal-${filterType}`;

  const initialOptions = options.slice(0, DEFAULT_VISIBLE_COUNT);
  const remainingOptions = options.slice(DEFAULT_VISIBLE_COUNT);

  const selectedFromRemaining = remainingOptions.filter((opt) => opt.isSelected);

  const visibleOptions = [...initialOptions, ...selectedFromRemaining];

  const hasMore = options.length > DEFAULT_VISIBLE_COUNT;
  const hasSelectedFilters = options.some((opt) => opt.isSelected);
  const isRatingFilter = filterType === 'ratingMin';

  const handleClearSection = () => {
    const currentParams = Object.fromEntries(searchParams?.entries() ?? []);
    const params = new URLSearchParams();

    Object.entries(currentParams).forEach(([key, value]) => {
      if (key !== filterType && key !== 'page') {
        params.set(key, String(value));
      }
    });

    const newUrl = buildListingHref(params);
    router.replace(newUrl, { scroll: false });
  };

  const handleShowMore = (event: React.MouseEvent<HTMLButtonElement>) => {
    showOverlay(modalKey);
    event.currentTarget.blur();
  };

  if (options.length === 0) {
    return null;
  }

  return (
    <>
      <FilterModal modalKey={modalKey} title={title} filterType={filterType} options={options} />
      <AccordionPrimitive.Root
        type='single'
        collapsible
        defaultValue={defaultOpen ? '0' : undefined}
      >
        <AccordionPrimitive.Item value='0' className='border-border-hover mb-4 border-b'>
          <div className='flex items-center justify-between'>
            <AccordionPrimitive.Trigger
              className={cn(
                'group flex flex-1 cursor-pointer items-center justify-between py-4 font-medium tracking-wide uppercase transition-all',
              )}
            >
              <span className='font-body-bold text-text text-xl'>{title}</span>
              <span className='text-text-muted relative h-5 w-5 shrink-0' aria-hidden='true'>
                <Plus className='absolute h-5 w-5 transition-opacity duration-200 group-data-[state=open]:opacity-0' />
                <Minus className='absolute h-5 w-5 opacity-0 transition-opacity duration-200 group-data-[state=open]:opacity-100' />
              </span>
            </AccordionPrimitive.Trigger>
            {showClear && hasSelectedFilters && (
              <button
                onClick={handleClearSection}
                className='ml-2 px-2 tracking-wide uppercase underline hover:no-underline'
                aria-label={`${t('clearAllFiltersFor')} ${title}`}
              >
                {t('clear')}
              </button>
            )}
          </div>
          <AccordionPrimitive.Content className='data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden transition-all'>
            <div className='space-y-1 pb-4'>
              {visibleOptions.map((option) =>
                isRatingFilter ? (
                  <RatingFilterItem
                    key={option.value}
                    filterType={filterType}
                    value={option.value}
                    count={option.count}
                    checked={option.isSelected}
                  />
                ) : (
                  <FilterCheckboxItem
                    key={option.value}
                    filterType={filterType}
                    value={option.value}
                    label={option.label}
                    count={option.count}
                    checked={option.isSelected}
                  />
                ),
              )}
              {hasMore && (
                <button
                  onClick={handleShowMore}
                  className='text-text hover:text-text-hover mt-2 tracking-wide uppercase'
                  aria-label={`${t('showAllOptionsFor')} ${title}`}
                >
                  {t('showMore')}
                </button>
              )}
            </div>
          </AccordionPrimitive.Content>
        </AccordionPrimitive.Item>
      </AccordionPrimitive.Root>
    </>
  );
};
